import { RefObject, useContext, useEffect } from 'react';

import CardContext from 'contexts/CardContext';
import HeadlessRefitContext from 'contexts/HeadlessRefitContext';

// Height reported for a table with no rows (loading / no data), matching the
// AG grid's, so its card still shrinks in the PDF but keeps the message
// readable
const MIN_CONTENT_PX = 120;

// Under the scheduled-report PDF print route, report the table's natural
// height so the canvas can refit the card to its content. The table lays
// itself out synchronously (no async column auto-size to wait for), so the
// first measurement is already the final one: no polling, no quiet window.
const useHeadlessHeight = ({
  isEnabled,
  tableRef,
  isEmpty,
}: {
  isEnabled: boolean;
  tableRef: RefObject<HTMLElement>;
  isEmpty: boolean;
}) => {
  const { i: cardId } = useContext(CardContext);
  const { reportContentHeight } = useContext(HeadlessRefitContext);

  useEffect(() => {
    if (!isEnabled || !cardId) {
      return;
    }

    const table = tableRef.current;
    if (isEmpty || !table) {
      reportContentHeight(cardId, MIN_CONTENT_PX);
      return () => reportContentHeight(cardId, undefined);
    }

    reportContentHeight(cardId, table.offsetHeight);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() =>
            reportContentHeight(cardId, table.offsetHeight),
          );
    observer?.observe(table);

    return () => {
      observer?.disconnect();
      reportContentHeight(cardId, undefined);
    };
  }, [cardId, isEmpty, isEnabled, reportContentHeight, tableRef]);
};

export default useHeadlessHeight;
